import { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, ShieldCheck, Sparkles, Users, Flame, Mic2, Star } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { usePageMeta } from "@/hooks/usePageMeta";
import { ALL_LOCATIONS } from "@/data/bookingOptions";
import { trackEvent, attachCampaign } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The 4 dedicated career tracks. Each gets its own page, copy and
// CareerApplication submission with `track` set to the slug.
const TRACKS = {
  "teacher-training": {
    icon: Users,
    tag: "Teach",
    title: "USDD Teacher Training",
    duration: "12–24 weeks",
    intro: "Apply for the next intake of the USDD Teacher Training programme. We'll review your background and invite you to a short audition + interview.",
    outcomes: ["USDD certification", "Paid teaching opportunities", "Studio placement"],
  },
  "professional-dancer": {
    icon: Flame,
    tag: "Perform",
    title: "Professional Dancer Track",
    duration: "1 Year · Full-Time",
    intro: "Apply for the full-time Professional Dancer Track. We're looking for serious dancers ready to commit to industry-level training and audition cycles.",
    outcomes: ["Audition coaching", "Reel & headshots", "Agency intros"],
  },
  "choreographer": {
    icon: Mic2,
    tag: "Create",
    title: "Choreographer Programme",
    duration: "20 weeks",
    intro: "Apply for the Choreographer Programme — concept, composition, direction and production fundamentals. Bring at least one finished piece (link).",
    outcomes: ["Original repertoire", "Showreel cut", "Industry mentor"],
  },
  "battle-athlete": {
    icon: Star,
    tag: "Compete",
    title: "Battle Athlete Path",
    duration: "Open enrolment",
    intro: "Apply for the Battle Athlete Path — for competitive dancers across breaking, hip-hop, popping, locking and all-styles.",
    outcomes: ["Crew placement", "Travel circuit", "Battle strategy"],
  },
};

const blankForm = {
  name: "",
  age: "",
  email: "",
  phone: "",
  location: "",
  preferred_start_date: "",
  dance_experience: "",
  why_applying: "",
  consent: false,
};

const Field = ({ label, children, testId }) => (
  <label data-testid={testId} className="block">
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2 font-bold">{label}</div>
    {children}
  </label>
);

export default function CareerApplicationPage() {
  const { track } = useParams();
  const meta = TRACKS[track];

  // Hooks must run unconditionally — even when the page is going to redirect.
  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  usePageMeta(
    meta ? `Apply · ${meta.title}` : "Career Track Application",
    meta ? `Apply for the ${meta.title} at USDD Academy. ${meta.intro}` : "Apply for one of the USDD career tracks."
  );

  const Icon = useMemo(() => (meta ? meta.icon : Users), [meta]);

  if (!meta) {
    // Unknown slug — bounce back to the homepage where Career Tracks live.
    return <Navigate to="/" replace />;
  }

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm consent to be contacted about your application.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(
        `${API}/enquiries/career-application`,
        attachCampaign({ ...form, track }),
      );
      trackEvent("career_application_submit", { track });
      // Re-uses the existing Meta Pixel "Lead" mapping via the regex trigger
      // covering referral_submit | private_coaching_quote → extend here too.
      trackEvent("lead", { kind: "career_application", track });
      setDone(true);
      toast.success("Application submitted — we'll be in touch within 3 working days.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't submit — try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <main className="bg-black">
        <PageHero
          eyebrow={`Career Tracks · ${meta.tag}`}
          title="Apply for"
          highlight={`${meta.title}.`}
          description={meta.intro}
          ctaPrimary={{ label: "Jump to application", to: "#career-application-form" }}
          ctaSecondary={{ label: "Back to all tracks", to: "/#career-tracks" }}
        />

        <section className="relative py-16 sm:py-20">
          <div className="absolute -top-32 right-1/4 w-[460px] h-[460px] rounded-full bg-brand-yellow/8 blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-32 left-1/4 w-[460px] h-[460px] rounded-full bg-brand-purpleLight/15 blur-[120px] pointer-events-none" />

          <div className="relative max-w-3xl mx-auto px-6 sm:px-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55 }}
              id="career-application-form"
              className="overflow-hidden rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep/30 via-zinc-950 to-black p-7 sm:p-10"
            >
              {done ? (
                <div data-testid="career-application-success" className="text-center py-6">
                  <div className="mx-auto w-14 h-14 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 flex items-center justify-center mb-4">
                    <Sparkles size={20} className="text-brand-yellow" />
                  </div>
                  <h2 className="font-display text-3xl text-white tracking-wide">
                    Application received.
                  </h2>
                  <p className="mt-3 text-sm text-zinc-300 max-w-md mx-auto">
                    Thanks for applying to the <span className="text-brand-yellow font-bold">{meta.title}</span>. We'll review and respond within 3 working days. Make sure <span className="text-white font-bold">{form.email}</span> isn't filtering our emails — replies come from <span className="text-brand-yellow">bookings@usddnetworks.co.uk</span>.
                  </p>
                  <Link
                    to="/"
                    className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-wider hover:bg-brand-yellowHover transition-all"
                    data-testid="career-application-home-link"
                  >
                    Back to home <ArrowRight size={14} />
                  </Link>
                </div>
              ) : (
                <form data-testid={`career-application-form-${track}`} onSubmit={onSubmit} className="space-y-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-11 h-11 rounded-xl bg-brand-yellow/10 border border-brand-yellow/40 flex items-center justify-center">
                      <Icon size={20} className="text-brand-yellow" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow font-bold">{meta.duration}</div>
                      <h2 className="font-display text-2xl sm:text-3xl text-white tracking-wide leading-none">{meta.title}</h2>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Full name *" testId="apply-name"><input data-testid="apply-name-input" required value={form.name} onChange={update("name")} className="ad-input" /></Field>
                    <Field label="Age" testId="apply-age"><input data-testid="apply-age-input" value={form.age} onChange={update("age")} className="ad-input" placeholder="e.g. 22" /></Field>
                    <Field label="Email *" testId="apply-email"><input data-testid="apply-email-input" required type="email" value={form.email} onChange={update("email")} className="ad-input" /></Field>
                    <Field label="Phone *" testId="apply-phone"><input data-testid="apply-phone-input" required value={form.phone} onChange={update("phone")} className="ad-input" /></Field>
                    <Field label="Location" testId="apply-location">
                      <select data-testid="apply-location-input" value={form.location} onChange={update("location")} className="ad-input">
                        <option value="">Pick a location</option>
                        {ALL_LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                        <option value="other">Other / remote</option>
                      </select>
                    </Field>
                    <Field label="Preferred start date" testId="apply-start"><input data-testid="apply-start-input" value={form.preferred_start_date} onChange={update("preferred_start_date")} className="ad-input" placeholder="e.g. Next intake / January 2026" /></Field>
                  </div>

                  <Field label="Dance experience" testId="apply-experience">
                    <textarea data-testid="apply-experience-input" value={form.dance_experience} onChange={update("dance_experience")} rows={4} className="ad-input" placeholder="Styles, years of training, teachers, performances, links to reels (optional)…" />
                  </Field>
                  <Field label="Why are you applying?" testId="apply-why">
                    <textarea data-testid="apply-why-input" value={form.why_applying} onChange={update("why_applying")} rows={4} className="ad-input" placeholder="What you want to get out of the programme…" />
                  </Field>

                  <label data-testid="apply-consent" className="flex items-start gap-3 text-xs text-zinc-300 mt-2">
                    <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 w-4 h-4 rounded border-white/30 bg-white/5" />
                    <span><span className="font-bold text-white">I consent to be contacted</span> about this application. See our <Link to="/privacy-policy" className="text-brand-yellow underline">Privacy &amp; GDPR Policy</Link>.</span>
                  </label>

                  <button
                    type="submit"
                    disabled={submitting || !form.consent}
                    data-testid={`apply-submit-${track}`}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover transition-all glow-yellow disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting && <Loader2 size={16} className="animate-spin" />}
                    Submit Application <ArrowRight size={16} />
                  </button>

                  <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500 pt-1">
                    <ShieldCheck size={11} /> Applications reviewed manually — typical response within 3 working days
                  </div>
                </form>
              )}
            </motion.div>

            <div className="mt-8 grid grid-cols-3 gap-3">
              {meta.outcomes.map((o) => (
                <div key={o} className="text-center text-[11px] uppercase tracking-wider text-zinc-300 bg-white/5 border border-white/10 rounded-2xl px-3 py-4">
                  {o}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <style>{`
        .ad-input { width:100%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12); border-radius: 12px; padding: 12px 14px; color: white; font-size: 14px; outline: none; }
        .ad-input:focus { border-color: #FFDA1F; box-shadow: 0 0 0 3px rgba(255,218,31,0.18); }
        .ad-input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
      <Footer />
    </>
  );
}
